import { z } from "zod";

/**
 * Kundendesign auf der Bühne (Customer View, D10): Logo und zwei Farben je
 * CRM-Kunde, gepflegt in der Kundenakte, per KI aus der Kunden-Website
 * vorgeschlagen. Spec: `docs/mindCoder/specs/2026-09-08-customer-view-modul-design.md`,
 * Abschnitt „Kundendesign auf der Bühne (D10, 2026-09-24)".
 */

/** 512 KB — ein Logo, kein Poster. Größere Dateien weist die API mit 413 ab. */
export const BRANDING_LOGO_MAX_BYTES = 524288;

/**
 * PNG und JPEG über die Signatur, SVG über eine serverseitige Allowlist —
 * bewusst die Ausnahme von „kein SVG" der Feedback-Screenshots, weil fast jede
 * Website ihr Logo so ausliefert und das Bild nur über `<img>` gerendert wird.
 */
export const BRANDING_LOGO_MIMES = ["image/png", "image/jpeg", "image/svg+xml"] as const;
export type BrandingLogoMime = (typeof BRANDING_LOGO_MIMES)[number];

/** Kleingeschriebenes Sechs-Stellen-Hex — eine Form, damit Vergleich und Kontrastrechnung nichts normalisieren müssen. */
export const hexColorSchema = z.string().regex(/^#[0-9a-f]{6}$/, "Farbe als #rrggbb (klein)");

/** Nur `https`, höchstens 500 Zeichen — die Adresse wird serverseitig abgerufen. */
export const websiteUrlSchema = z
  .string()
  .trim()
  .max(500)
  .url()
  .refine((u) => u.startsWith("https://"), "Website muss mit https:// beginnen");

export const brandingSourceSchema = z.enum(["ki", "manuell"]);
export type BrandingSource = z.infer<typeof brandingSourceSchema>;

export const brandingSchema = z.object({
  customerId: z.string().uuid(),
  websiteUrl: z.string().nullable(),
  hasLogo: z.boolean(),
  logoMime: z.enum(BRANDING_LOGO_MIMES).nullable(),
  primaryColor: hexColorSchema.nullable(),
  accentColor: hexColorSchema.nullable(),
  source: brandingSourceSchema.nullable(),
  /** ISO-Zeitpunkt des letzten KI-Vorschlags; null ohne. */
  suggestedAt: z.string().nullable(),
  /** ISO-Zeitpunkt der letzten Änderung; null, solange es keinen Datensatz gibt. */
  updatedAt: z.string().nullable(),
});
export type Branding = z.infer<typeof brandingSchema>;

/** Was der Lead von Hand setzt — jedes Feld optional, `null` löscht den Wert. */
export const brandingUpdateSchema = z
  .object({
    websiteUrl: websiteUrlSchema.nullable().optional(),
    primaryColor: hexColorSchema.nullable().optional(),
    accentColor: hexColorSchema.nullable().optional(),
  })
  .strict();
export type BrandingUpdate = z.infer<typeof brandingUpdateSchema>;

export const brandingSuggestResponseSchema = z.object({
  branding: brandingSchema,
  /** Was der Vorschlag gefunden hat — der Lead sieht sofort, was er noch selbst setzen muss. */
  found: z.object({ logo: z.boolean(), primaryColor: z.boolean(), accentColor: z.boolean() }),
  /** false = deterministischer Rückfall ohne LLM (nicht konfiguriert oder Aufruf gescheitert). */
  viaLlm: z.boolean(),
});
export type BrandingSuggestResponse = z.infer<typeof brandingSuggestResponseSchema>;

/**
 * Das Design, wie es die Bühne braucht. `logoUrl` ist same-origin
 * (`/api/v1/customers/:id/branding/logo?v=<updatedAt>`) — das Bühnenfenster
 * ist im selben Browser angemeldet.
 */
export const stageBrandingSchema = z.object({
  logoUrl: z.string().nullable(),
  primaryColor: hexColorSchema.nullable(),
  accentColor: hexColorSchema.nullable(),
});
export type StageBranding = z.infer<typeof stageBrandingSchema>;
